import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getMyStudentProfile } from "../../../services/StudentProfileService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getExams } from "../../../services/ExamService";
import { startAttempt } from "../../../services/StudentAttemptService";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../utils/i18nLabels";

const AvailableExams = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [startingId, setStartingId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const profileRes = await getMyStudentProfile();
        const myProfile = profileRes.data?.data;
        setProfile(myProfile);

        const params = { page: 1, limit: 100 };
        if (myProfile?.class_id) params.class_id = myProfile.class_id;

        const [scheduledRes, ongoingRes, examsRes] = await Promise.all([
          getExamSessions({ ...params, status: "scheduled" }),
          getExamSessions({ ...params, status: "ongoing" }),
          getExams({ page: 1, limit: 200 }),
        ]);

        setSessions([...(scheduledRes.data?.data || []), ...(ongoingRes.data?.data || [])]);
        setExams(examsRes.data?.data || []);
      } catch (err) {
        if (err?.response?.status === 404) {
          setLoadError(t("pages.availableExams.studentOnlyPage"));
        } else {
          setLoadError(err?.response?.data?.message || t("pages.availableExams.loadError"));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const examFor = (session) => exams.find((e) => e.id === session.exam_id);

  const isOpen = (session) => {
    const now = new Date();
    return now >= new Date(session.start_at) && now <= new Date(session.end_at);
  };

  const handleStart = async (session) => {
    if (!profile) return;
    setStartingId(session.id);
    setRowErrors((prev) => ({ ...prev, [session.id]: undefined }));
    try {
      const res = await startAttempt({ exam_session_id: session.id, student_id: profile.id });
      const attempt = res.data?.data;
      navigate(`/take-exam/${attempt.id}`, { state: { session, exam: examFor(session) } });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [session.id]: err?.response?.data?.message || t("pages.availableExams.startError"),
      }));
    } finally {
      setStartingId(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.myExams")}</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4 className="mb-0">{t("common.ui.examensDisponibles")}</h4>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t("common.crud.loading")}</span>
                </div>
              </div>
            ) : loadError ? (
              <div className="alert alert-warning">{loadError}</div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-4">
                <i className="ti ti-calendar-off fs-1 text-muted"></i>
                <h5 className="mt-2">{t("pages.availableExams.noExamsAvailable")}</h5>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead className="thead-light">
                    <tr>
                      <th>{t("common.ui.examen")}</th>
                      <th>{t("common.ui.duree")}</th>
                      <th>{t("pages.availableExams.openFrom")}</th>
                      <th>{t("pages.availableExams.openTo")}</th>
                      <th>{t("common.crud.actionColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => {
                      const exam = examFor(session);
                      const open = isOpen(session);
                      return (
                        <tr key={session.id}>
                          <td className="fw-medium">{exam?.title || "-"}</td>
                          <td>{t("common.ui.min", { duration_minutes: exam?.duration_minutes })}</td>
                          <td>{new Date(session.start_at).toLocaleString(getLocale(), { timeZone: "Africa/Nouakchott" })}</td>
                          <td>{new Date(session.end_at).toLocaleString(getLocale(), { timeZone: "Africa/Nouakchott" })}</td>
                          <td>
                            {open ? (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => handleStart(session)}
                                disabled={startingId === session.id}
                              >
                                {startingId === session.id ? t("pages.availableExams.starting") : t("pages.availableExams.start")}
                              </button>
                            ) : (
                              <span className="badge badge-soft-secondary">{t("pages.availableExams.notYetOpen")}</span>
                            )}
                            {rowErrors[session.id] && (
                              <div className="text-danger small mt-1">{rowErrors[session.id]}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailableExams;

